import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { GustoMemberData } from '../../types';
import { formatDateTime } from '../../utils/formatters';

const getErrorStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status;
  }
  return undefined;
};

interface GustoInfoCardProps {
  data: GustoMemberData | undefined;
  isLoading: boolean;
  error: unknown;
  /**
   * Prefix for the data-testid attributes so the card can be reused on
   * different pages without id collisions (e.g. "my-gusto-info",
   * "member-gusto-info").
   */
  testIdPrefix: string;
  /** i18n key for the card description shown under the title. */
  descriptionKey: string;
  /** i18n key for the description shown in the not-connected empty state. */
  notConnectedDescriptionKey: string;
}

/**
 * GustoInfoCard
 * Reusable read-only card that renders a member's Gusto payroll/employment
 * data. All fields except the member/company identifiers are optional and fall
 * back to a "not available" placeholder, matching the /api/gusto/me contract.
 * The container that renders it owns data fetching (self vs. specific member).
 */
export const GustoInfoCard: React.FC<GustoInfoCardProps> = ({
  data,
  isLoading,
  error,
  testIdPrefix,
  descriptionKey,
  notConnectedDescriptionKey,
}) => {
  const { t } = useTranslation('people');
  const cardTestId = `${testIdPrefix}-card`;

  if (isLoading) {
    return (
      <Card data-testid={cardTestId}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <CardTitle>{t('detail.gusto.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid={`${cardTestId}-loading`} />
        </CardContent>
      </Card>
    );
  }

  const isNotConnected = getErrorStatus(error) === 404 || (!!error && !data);

  if (isNotConnected || !data) {
    return (
      <Card data-testid={cardTestId}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <CardTitle>{t('detail.gusto.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center space-y-1" data-testid={`${cardTestId}-empty`}>
            <p className="text-sm font-medium">{t('detail.gusto.notConnected')}</p>
            <p className="text-sm text-muted-foreground">{t(notConnectedDescriptionKey)}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const na = t('detail.gusto.notAvailable');

  return (
    <Card data-testid={cardTestId}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          <CardTitle>{t('detail.gusto.title')}</CardTitle>
        </div>
        <CardDescription>{t(descriptionKey)}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-name`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.name')}</span>
            <span className="font-medium">{data.name || na}</span>
          </div>
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-email`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.email')}</span>
            <span className="font-medium">{data.email || na}</span>
          </div>
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-gusto-type`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.gustoType')}</span>
            <span className="font-medium">{data.gustoType || na}</span>
          </div>
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-job-title`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.jobTitle')}</span>
            <span className="font-medium">{data.jobTitle || na}</span>
          </div>
          {data.onboardingStatus ? (
            <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-onboarding-status`}>
              <span className="text-muted-foreground">{t('detail.gusto.fields.onboardingStatus')}</span>
              <span className="font-medium">{data.onboardingStatus}</span>
            </div>
          ) : null}
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-status`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.status')}</span>
            <span className="font-medium">
              {data.isActive ? t('detail.gusto.active') : t('detail.gusto.inactive')}
            </span>
          </div>
          <div className="flex justify-between text-sm" data-testid={`${testIdPrefix}-synced-at`}>
            <span className="text-muted-foreground">{t('detail.gusto.fields.syncedAt')}</span>
            <span className="font-medium">
              {data.gustoSyncedAt ? formatDateTime(data.gustoSyncedAt) : na}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
